import React, { useEffect, useState } from 'react';
import './LoginPage.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SharkHeader from '../components/SharkHeader';
import { FaUser, FaLock } from 'react-icons/fa';
import { getLocalizedText } from '../i18n/loginTranslations';

const isLikelyEmail = (value) => value.includes('@');

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const APP_CONFIG = {
  defaultLoginApiUrl: process.env.REACT_APP_ALEXA_LOGIN_API_DEFAULT || '',
  euLoginApiUrl: process.env.REACT_APP_ALEXA_LOGIN_API_EU || process.env.REACT_APP_ALEXA_LOGIN_API_DEFAULT || '',
  naRedirectUri: process.env.REACT_APP_ALEXA_REDIRECT_URI_NA || '',
  euRedirectUri: process.env.REACT_APP_ALEXA_REDIRECT_URI_EU || '',
};

const normalizeUri = (value) => (value || '').trim().replace(/\/+$/, '').toLowerCase();

const getLoginApiUrlForRedirectUri = (redirectUri) => {
  const normalized = normalizeUri(redirectUri);
  if (normalized === normalizeUri(APP_CONFIG.euRedirectUri)) {
    return APP_CONFIG.euLoginApiUrl;
  }
  if (normalized === normalizeUri(APP_CONFIG.naRedirectUri)) {
    return APP_CONFIG.defaultLoginApiUrl;
  }
  return APP_CONFIG.defaultLoginApiUrl;
};

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const t = getLocalizedText();

  const authProviders = [
    { id: 'google', label: 'Google', url: process.env.REACT_APP_GOOGLE_SIGNIN_URL || '' },
    { id: 'amazon', label: 'Amazon', url: process.env.REACT_APP_AMAZON_SIGNIN_URL || '' },
    { id: 'facebook', label: 'Facebook', url: process.env.REACT_APP_FACEBOOK_SIGNIN_URL || '' },
  ].filter((provider) => Boolean(provider.url));

  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (window.location.protocol !== 'https:' && !isLocalhost) {
      window.location.replace(`https://${window.location.host}${window.location.pathname}${window.location.search}`);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedUsername = username.trim();
    const hasPasswordEdgeSpaces = password !== password.trim();

    if (!trimmedUsername || !password) {
      setError(t.missingFields);
      return;
    }

    if (username !== trimmedUsername) {
      setError(t.usernameTrim);
      return;
    }

    if (hasPasswordEdgeSpaces) {
      setError(t.passwordSpaces);
      return;
    }

    if (isLikelyEmail(trimmedUsername) && !isValidEmail(trimmedUsername)) {
      setError(t.invalidEmail);
      return;
    }

    setIsProcessing(true);

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const clientId = queryParams.get('clientId') || queryParams.get('client_id') || '';
      const responseType = queryParams.get('response_type') || '';
      const scope = queryParams.get('scope') || '';
      const redirectUri = queryParams.get('redirect_uri') || '';
      const state = queryParams.get('state') || '';

      if (!APP_CONFIG.defaultLoginApiUrl || !APP_CONFIG.naRedirectUri || !APP_CONFIG.euRedirectUri) {
        setError(t.configError);
        setIsProcessing(false);
        return;
      }

      const requestUrl = getLoginApiUrlForRedirectUri(redirectUri);
      if (!requestUrl.startsWith('https://')) {
        throw new Error('Insecure transport blocked');
      }

      const requestPayload = {
        username: trimmedUsername,
        password,
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType || 'code',
        state,
      };

      if (scope) {
        requestPayload.scope = scope;
      }

      const response = await axios.post(requestUrl, requestPayload);

      const redirectURI = response.data.redirectURI;
      sessionStorage.setItem('redirectURI', redirectURI);
      navigate('/consent');
    } catch (err) {
      setError(t.invalidCredentials);
    } finally {
      setIsProcessing(false);
    }
  };


  //   const handleBack = () => {
  //     window.history.back(); // Go to previous page
  //   };

  return (
    <div className="login-shell">
      <div className="container">
        {/* <button className="back-button" onClick={handleBack}>←</button> */}
        <SharkHeader />
        <h1 className="title">{t.title}</h1>
        <p className="subtitle">{t.subtitle}</p>
        <p className="instruction">{t.instruction}</p>
        <form className="login-form" onSubmit={handleLogin} autoComplete="off">
          <div className="input-group">
            <FaUser className="input-icon" />
            <input
              type="text"
              placeholder={t.usernameLabel}
              aria-label={t.usernameLabel}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="email"
              maxLength={320}
            />
          </div>
          <div className="input-group">
            <FaLock className="input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t.passwordLabel}
              aria-label={t.passwordLabel}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={128}
            />
          </div>
          <div className="show-password">
            <input
              type="checkbox"
              id="showPassword"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            <label htmlFor="showPassword">{t.showPassword}</label>
          </div>
          {error && (
            <div className="error-box" role="alert" aria-live="polite">
              <div className="error-title">{t.loginFailedTitle}</div>
              <div className="error-description">{error}</div>
            </div>
          )}
          <button type="submit" className="login-button" disabled={isProcessing}>
            {isProcessing ? t.processing : t.login}
          </button>
          {authProviders.length > 0 && (
            <div className="provider-section">
              <p className="provider-title">{t.otherMethods}</p>
              <div className="provider-buttons">
                {authProviders.map((provider) => (
                  <a key={provider.id} href={provider.url} className="provider-link">
                    {provider.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
